import React from "react"
import { Tooltip } from "react-tooltip"
import { useTranslation } from "react-i18next"
import { useTable, useSortBy, usePagination, useFilters, useGlobalFilter } from "react-table"

import { useAuth } from "hooks/useAuth"

import Emoji from "components/shared/Emoji"
import { userPermissionList, profilStatusList } from "utils/constants"
import { EditIcon, TrashIcon } from "components/Elements/Icons"

const PAGINATION_LENGTH = 10

export const TeamTable = ({
	data,
	permissionList = userPermissionList,
	openFormProfil,
	deleteProfil,
	className,
}) => {
	const { t } = useTranslation()
	const { user } = useAuth()

	// Columns definition
	const columns = React.useMemo(
		() => [
			{
				Header: t("profil.name", { ns: "team" }),
				accessor: "name",
			},
			{
				Header: t("profil.firstname", { ns: "team" }),
				accessor: "firstname",
			},
			{
				Header: t("profil.mail", { ns: "team" }),
				accessor: "mail",
			},
			{
				Header: t("profil.role", { ns: "team" }),
				accessor: (originalRow) =>
					t(`${permissionList.find((p) => p.value === originalRow.role)?.label}`),
			},
			{
				Header: t("profil.status", { ns: "team" }),
				accessor: (originalRow) =>
					t(`${profilStatusList.find((p) => p.id === originalRow.status)?.label}`),
			},
		],
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[t]
	)

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		prepareRow,
		// rows,
		page, // the rows for the active page

		// pagination utilities
		canPreviousPage,
		canNextPage,
		pageOptions,
		pageCount,
		gotoPage,
		setGlobalFilter,
		state: { pageIndex },
	} = useTable(
		{
			columns,
			data,
			initialState: {
				pageSize: 10,
			},
			// autoResetSelectedRows: false
		},
		useFilters,
		useGlobalFilter,
		useSortBy,
		usePagination
	)

	const getColumnSize = React.useCallback(
		(columnName) => {
			// sum should be 24
			switch (columnName) {
				case columns[0].accessor:
				case columns[1].accessor:
				case "actions":
					return "c-table__cell--3"
				case columns[2].accessor:
					return "c-table__cell--6"
				case t("team:profil.role"):
					return "c-table__cell--4"
				case t("team:profil.status"):
					return "c-table__cell--5"
				default:
					return "flex-initial"
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		},
		[columns]
	)

	// Manage global search
	// -------------------
	const [dataTableFilter, setDataTableFilter] = React.useState("")
	React.useEffect(() => setGlobalFilter(dataTableFilter), [dataTableFilter, setGlobalFilter])

	// Manage pagination
	// -------------------
	const middleDistance = Math.round((PAGINATION_LENGTH - 1) / 2)
	const pageShouldBeHiddenInPagination = (index) => {
		// in the first pages,
		// pagination should not exceed PAGINATION_LENGTH
		const condition1 = pageIndex <= middleDistance && index > PAGINATION_LENGTH - 1
		// in the middle pages,
		// show x pages before currentPage and y pages after currentPage
		const condition2 =
			pageIndex > middleDistance &&
			pageIndex < pageOptions.length - middleDistance &&
			(index < pageIndex - middleDistance || index > pageIndex + middleDistance - 1)
		// in the last pages,
		// always show the x last pages
		const condition3 =
			pageIndex > pageOptions.length - 1 - middleDistance &&
			index < pageOptions.length - PAGINATION_LENGTH
		return condition1 || condition2 || condition3
	}

	return (
		<>
			<div className={"c-table-team " + (className ? className : "")}>
				<div className="c-table-team__header md:mb-0 pb-7">
					<div className="flex flex-col md:flex-row justify-between mb-7 md:mb-0">
						<div className="flex-1">
							{/* Global filter */}
							<div className={"c-form-group c-form-group--picto mb-7 md:mb-0"}>
								<label htmlFor="tableglobalFilter" className="c-label mb-1">
									{t("table.search", { ns: "team" })}
								</label>
								<svg
									className="c-input-picto"
									xmlns="http://www.w3.org/2000/svg"
									height="20"
									width="20"
									viewBox="0 0 512 512"
								>
									<path d="M505.749 475.587l-145.6-145.6c28.203-34.837 45.184-79.104 45.184-127.317C405.333 90.926 314.41.003 202.666.003S0 90.925 0 202.669s90.923 202.667 202.667 202.667c48.213 0 92.48-16.981 127.317-45.184l145.6 145.6c4.16 4.16 9.621 6.251 15.083 6.251s10.923-2.091 15.083-6.251c8.341-8.341 8.341-21.824-.001-30.165zM202.667 362.669c-88.235 0-160-71.765-160-160s71.765-160 160-160 160 71.765 160 160-71.766 160-160 160z" />
								</svg>
								<input
									className={"c-input pl-12"}
									id={"tableglobalFilter"}
									name={"tableglobalFilter"}
									type={"text"}
									placeholder={`${t("store.contact_name", {
										ns: "partner",
									})}, ${t("store.contact_firstname", { ns: "partner" })}, ...`}
									value={dataTableFilter}
									style={{ maxWidth: "400px" }}
									onChange={(event) => setDataTableFilter(event.target.value)}
								/>
							</div>
						</div>
						{user?.permission !== 1 && (
							<div className="">
								<button
									className="c-btn c-btn--primary px-12"
									onClick={() => openFormProfil({})}
								>
									{t("add", { ns: "team" })}
								</button>
							</div>
						)}
					</div>
				</div>

				<table className="c-table c-table--responsive" {...getTableProps()}>
					<thead>
						{headerGroups.map((headerGroup) => (
							<tr className="c-table__row" {...headerGroup.getHeaderGroupProps()}>
								{headerGroup.headers.map((column) => {
									return (
										<th
											className={
												"c-table__cell p-5 " + getColumnSize(column?.id)
											}
											{...column.getHeaderProps(
												column.getSortByToggleProps()
											)}
										>
											<span className={"u-ellipsis"}>
												{column.render("Header")}
											</span>
											<span>
												{column.isSorted
													? column.isSortedDesc
														? " 🔽"
														: " 🔼"
													: ""}
											</span>
										</th>
									)
								})}
							</tr>
						))}
					</thead>
					<tbody {...getTableBodyProps()}>
						{page.map((row) => {
							const isActive = user?.user_id === row.original?.userid
							const hasAdminRights = user?.permission === -1 || user?.permission === 0 // admin or manager
							prepareRow(row)
							return (
								<tr
									className={
										"c-table__row p-5 lg:p-0 " +
										(isActive ? "font-bold text-primary" : "")
									}
									{...row.getRowProps()}
								>
									{row.cells.map((cell) => {
										return (
											<td
												className={
													"c-table__cell md:p-5 " +
													getColumnSize(cell?.column?.id)
												}
												{...cell.getCellProps()}
											>
												<span className={"u-ellipsis"}>
													{cell.render("Cell")}
												</span>
											</td>
										)
									})}
									<td
										className={
											"c-table__cell u-relative text-right md:p-5 " +
											getColumnSize("actions")
										}
									>
										{hasAdminRights && row.original?.iseditable === true && (
											<>
												<button
													className="mx-2"
													data-tooltip-content={t("global.update")}
													data-tooltip-id="team-table"
													aria-label={t("global.update")}
													onClick={() => openFormProfil(row?.original)}
												>
													<EditIcon size={20} />
												</button>

												<button
													className="text-danger mx-2"
													data-tooltip-content={t("table.delete", {
														ns: "team",
													})}
													data-tooltip-id="team-table"
													aria-label={t("table.delete", { ns: "team" })}
													onClick={() => deleteProfil(row?.original)}
												>
													<TrashIcon size={20} />
												</button>
											</>
										)}
									</td>
								</tr>
							)
						})}
					</tbody>
				</table>

				{data.length === 0 && (
					<p className="text-center mt-5">
						{t("table.no_result", { ns: "team" })}{" "}
						<Emoji label="visage triste" symbol="😟" />
					</p>
				)}

				{/* Pagination */}
				{pageOptions && pageOptions.length > 1 && (
					<div className="flex justify-center mt-5">
						<div className="c-pagination">
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(0)}
								disabled={!canPreviousPage}
							>
								{"<<"}
							</button>
							{pageOptions.map((index) => {
								if (pageShouldBeHiddenInPagination(index))
									return <React.Fragment key={index}></React.Fragment>
								return (
									<button
										type="button"
										key={index}
										className={index === pageIndex ? "font-bold" : ""}
										style={{ width: "30px" }}
										onClick={() => gotoPage(index)}
									>
										{index + 1}
									</button>
								)
							})}
							<button
								className="c-btn c-btn--primary px-2 mx-5"
								onClick={() => gotoPage(pageCount - 1)}
								disabled={!canNextPage}
							>
								{">>"}
							</button>
						</div>
					</div>
				)}
			</div>
			<Tooltip id="team-table" />
		</>
	)
}
